import { useBounds } from "@react-three/drei";
import { invalidate } from "@react-three/fiber";
import { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import type { ThreeSidePathsType } from "@tosize/shapes/src/types/three";
import { getLocaleAsset } from "@tosize/ui";
import gsap from "gsap";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
	DoubleSide,
	Euler,
	type Group,
	MathUtils,
	MeshPhongMaterial,
	MeshStandardMaterial,
	RepeatWrapping,
	TextureLoader,
	Vector3,
} from "three";
import { P, match } from "ts-pattern";
import type { ExtendedVisuProps } from "../..";
import type { CanvasSettingType } from "../../CanvasSettings";
import { usePanelVisualisation } from "../../Context";
import { InputLines } from "../lines/InputLine";
import { SideLine } from "../lines/SideLines";
import { FONT_SIZE } from "../lines/utils";
import { Drillholes } from "./Drillholes";
import { Finish } from "./Finish";
import { Outline } from "./Outline";
import { Side } from "./Side";
import { Top } from "./Top";

export const RENDER_QUALITY = 30;

interface GenericShapeProps
	extends Pick<ExtendedVisuProps, "modus" | "finishInputs" | "grainType"> {
	canvasSettings: CanvasSettingType;
}

export function GenericShape(props: GenericShapeProps) {
	const { modus, finishInputs, grainType, canvasSettings } = props;
	const { sidePaths, inputLines, length, width, thickness } = usePanelVisualisation();

	const sideMaterial = useCallback((active: boolean) => {
		return new MeshStandardMaterial({
			color: active ? "#FFCC33" : "#9AC8DB",
			side: DoubleSide,
			transparent: true,
			opacity: 0.35,
		});
	}, []);

	const lidMaterial = useCallback(
		(active: boolean) => {
			const texture = match(grainType)
				.with("lengthwise", () => {
					const image = getLocaleAsset("tosize_furniture/lengthwise_grain.png");
					const texture = new TextureLoader().load(image);

					texture.wrapS = texture.wrapT = RepeatWrapping;
					texture.repeat.set(1, 16);

					return texture;
				})
				.with("broadwise", () => {
					const image = getLocaleAsset("tosize_furniture/broadwise_grain.png");
					const texture = new TextureLoader().load(image);

					texture.wrapS = texture.wrapT = RepeatWrapping;
					texture.repeat.set(16, 1);

					return texture;
				})
				.otherwise(() => null);

			return new MeshPhongMaterial({
				color: active ? "#FFCC33" : "#9AC8DB",
				side: DoubleSide,
				transparent: true,
				opacity: 0.45,
				shininess: 3,
				map: texture,
			});
		},
		[grainType],
	);

	const bounds = useBounds();
	const ref = useRef<Group>(null!);

	const currentFieldView = match(canvasSettings)
		.with({ view: P.any, field: "top" }, () => ({
			position: {
				x: -length / 1000 / 2,
				y: -width / 1000 / 2,
				z: -Number(thickness) / 1000 / 2,
			},
			rotation: {
				x: MathUtils.degToRad(0),
				y: MathUtils.degToRad(0),
				z: MathUtils.degToRad(0),
			},
		}))
		.with({ view: P.any, field: "bottom" }, () => ({
			position: {
				x: length / 1000 / 2,
				y: -width / 1000 / 2,
				z: Number(thickness) / 1000 / 2,
			},
			rotation: {
				x: MathUtils.degToRad(0),
				y: MathUtils.degToRad(180),
				z: MathUtils.degToRad(0),
			},
		}))
		.with({ view: "detail", field: "length1" }, () => ({
			position: {
				x: length / 1000 / 2,
				y: -Number(thickness) / 1000 / 2,
				z: width / 1000 / 2,
			},
			rotation: {
				x: MathUtils.degToRad(-90),
				y: MathUtils.degToRad(0),
				z: MathUtils.degToRad(180),
			},
		}))
		.with({ view: "detail", field: "length2" }, () => ({
			position: {
				x: -length / 1000 / 2,
				y: -Number(thickness) / 1000 / 2,
				z: width / 1000 / 2,
			},
			rotation: {
				x: MathUtils.degToRad(-90),
				y: MathUtils.degToRad(0),
				z: MathUtils.degToRad(0),
			},
		}))
		.with({ view: "detail", field: "width1" }, () => ({
			position: {
				x: width / 1000 / 2,
				y: -Number(thickness) / 1000 / 2,
				z: length / 1000 / 2,
			},
			rotation: {
				x: MathUtils.degToRad(-90),
				y: MathUtils.degToRad(0),
				z: MathUtils.degToRad(90),
			},
		}))
		.with({ view: "detail", field: "width2" }, () => ({
			position: {
				x: -width / 1000 / 2,
				y: -Number(thickness) / 1000 / 2,
				z: -length / 1000 / 2,
			},
			rotation: {
				x: MathUtils.degToRad(-90),
				y: MathUtils.degToRad(0),
				z: MathUtils.degToRad(-90),
			},
		}))
		.otherwise(() => {
			return match(canvasSettings.view)
				.with("3d", () => ({
					position: {
						x: -length / 2 / 1000,
						y: 0,
						z: 0,
					},
					rotation: {
						x: MathUtils.degToRad(-60),
						y: MathUtils.degToRad(0),
						z: MathUtils.degToRad(-25),
					},
				}))
				.otherwise(() => ({
					position: {
						x: -length / 2 / 1000,
						y: -width / 2 / 1000,
						z: 0,
					},
					rotation: {
						x: MathUtils.degToRad(0),
						y: MathUtils.degToRad(0),
						z: MathUtils.degToRad(0),
					},
				}));
		});

	useEffect(() => {
		if (!ref.current) return;

		const pos = currentFieldView.position;
		const rot = currentFieldView.rotation;

		const positionIsNotEqual = !ref.current.position.equals(new Vector3(pos.x, pos.y, pos.z));
		const rotationIsNotEqual = !ref.current.rotation.equals(new Euler(rot.x, rot.y, rot.z));

		const positionTween = positionIsNotEqual
			? gsap.to(ref.current.position, {
					x: pos.x,
					y: pos.y,
					z: pos.z,
					duration: 0.8,
					onUpdate: () => {
						invalidate();
						bounds.refresh().clip().fit();
					},
					onComplete: () => {
						invalidate();
						bounds.refresh().clip().fit();
					},
				})
			: null;

		const rotationTween = rotationIsNotEqual
			? gsap.to(ref.current.rotation, {
					x: rot.x,
					y: rot.y,
					z: rot.z,
					duration: 0.8,
					onUpdate: () => {
						invalidate();
						bounds.refresh().clip().fit();
					},
					onComplete: () => {
						invalidate();
						bounds.refresh().clip().fit();
					},
				})
			: null;

		return () => {
			positionTween?.kill();
			rotationTween?.kill();
		};
	}, [currentFieldView, bounds]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: should run when sidePaths or inputLines change
	useEffect(() => {
		const timeout = setTimeout(() => bounds.refresh().clip().fit(), 100);

		return () => clearTimeout(timeout);
	}, [bounds, sidePaths, inputLines]);

	const sideLines = useMemo(() => {
		const lines: ThreeSidePathsType[] = [];

		if (sidePaths) {
			for (const line of sidePaths) {
				if (+line.path.getLength().toFixed(10) === 0) {
					continue;
				}

				const existingLine = lines.find((existing) => {
					return line?.group !== undefined && existing?.group === line?.group;
				});

				if (existingLine === undefined) {
					const newLine = { ...line, path: line.path.clone(), groupCodes: [line.code] };

					if (line?.group !== undefined) {
						newLine.group = line.group;
					}
					lines.push(newLine);
				} else {
					existingLine.groupCodes?.push(line.code);

					const points = line.path.getPoints(RENDER_QUALITY);
					if (points.length > 1) {
						existingLine.path.curves.push(...line.path.curves);
					}
				}
			}
		}

		return lines;
	}, [sidePaths]);

	const minLength = useMemo(
		() => Math.min(...sideLines.map((sideLine) => sideLine.path.getLength())),
		[sideLines],
	);

	const fontSize = useMemo(() => {
		const lengths = inputLines?.map((inputLine) => inputLine.path.getLength()).filter(Number);
		const minLength = lengths ? Math.min(...lengths) : 0;

		return Math.max(FONT_SIZE * minLength, FONT_SIZE * 0.6);
	}, [inputLines]);

	return (
		<group
			ref={ref}
			name="shape"
			position={[-length / 2 / 1000, -width / 2 / 1000, 0]}
			rotation={[0, 0, 0]}
			scale={[1, 1, 2]}
		>
			<group name="outline">
				{sidePaths?.map((side, key) => (
					<Outline key={key} path={side.path} thickness={thickness} />
				))}
			</group>

			<group name="sidePaths">
				<Top
					paths={sidePaths}
					zPosition={thickness}
					material={lidMaterial(canvasSettings.field === SideName.Top)}
				/>

				<Top
					paths={sidePaths}
					zPosition={0}
					material={lidMaterial(canvasSettings.field === SideName.Bottom)}
				/>

				{sidePaths?.map((side, key) => (
					<Side
						side={side}
						key={key}
						height={thickness}
						material={sideMaterial(side.code === canvasSettings.field)}
					/>
				))}
			</group>

			{modus === "finishes" && (
				<group name="finishes">
					{sideLines.map((sideLine, key) => (
						<Finish key={key} side={sideLine} minLength={minLength} />
					))}
				</group>
			)}

			<Drillholes currentField={canvasSettings.field} />

			{modus === "dimensions" && (
				<group name="dimension-lines">
					<InputLines currentField={canvasSettings.field} />
				</group>
			)}

			{modus === "finishes" && (
				<group name="finish-lines">
					{sideLines.map((sideLine, index) => (
						<SideLine
							key={index}
							side={sideLine}
							finishInputs={finishInputs}
							fontSize={fontSize}
							currentField={canvasSettings.field}
						/>
					))}
				</group>
			)}
		</group>
	);
}
